import axios, { HttpStatusCode, InternalAxiosRequestConfig } from "axios";

import { getLocalValue, setLocalValue } from "./localStorage";

let isReissue = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: any = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
  isReissue = false;
};

const request = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/v2/app`,
});

export const authRequest = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const requestSuccess = async (config: InternalAxiosRequestConfig<any>) => {
  const tokenString = getLocalValue("jwtToken");
  if (!tokenString) return config;

  const jwtToken = JSON.parse(tokenString);
  if (jwtToken && jwtToken.accessToken) {
    config.headers.Authorization = `Bearer ${jwtToken.accessToken}`;
  } else {
    throw new Error("accessToken is not exist");
  }
  return config;
};

export const reissueToken = async (jwtToken: any) => {
  if (typeof jwtToken !== "object") {
    throw new Error(`jwtToken is not object`);
  }
  if (!jwtToken?.refreshToken) {
    throw new Error(`refreshToken is not exist`);
  }

  const response = await authRequest.post("oauth2/reissue", null, {
    headers: {
      Authorization: "Bearer " + jwtToken.refreshToken,
    },
  });
  if (response.status === HttpStatusCode.Ok) {
    setLocalValue(
      "jwtToken",
      JSON.stringify({
        grantType: response.data.grantType,
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
      })
    );
  } else {
    throw new Error("reissueToken response status error");
  }
  return response;
};

const responseError = async (error: any) => {
  if (navigator.onLine === false) {
    const networkError = {
      ...error,
      response: { data: { error: "네트워크 연결이 필요합니다." } },
    };
    return Promise.reject(networkError);
  }

  const originalRequest = error.config;

  if (
    error.response &&
    error.response.status === HttpStatusCode.Unauthorized &&
    !originalRequest._retry
  ) {
    originalRequest._retry = true;

    if (isReissue) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token: any) => {
          originalRequest.headers.Authorization = `Bearer ${token.accessToken}`;
          return request.request(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    isReissue = true;

    try {
      const jwtTokenString = getLocalValue("jwtToken");

      if (!jwtTokenString) return Promise.reject(error);

      const jwtToken = JSON.parse(jwtTokenString);
      const response = await reissueToken(jwtToken);

      processQueue(null, response.data.accessToken);

      // 갱신된 액세스 토큰으로 원래 요청을 재시도합니다.
      originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
      return request.request(originalRequest);
    } catch (err: any) {
      const autoToken = getLocalValue("AUTO");
      if (autoToken) {
        try {
          const response = await reissueToken({ refreshToken: autoToken });

          processQueue(null, response.data.accessToken);

          originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
          return request.request(originalRequest);
        } catch (e: any) {
          // AUTO reissue also failed
          processQueue(e, null);
        }
      } else {
        // No AUTO token
        processQueue(err, null);
      }
    }
  }

  return Promise.reject(error);
};

// 요청 인터셉터를 설정하여 요청 전에 액세스 토큰을 추가합니다.
request.interceptors.request.use(requestSuccess, (error) => {
  return Promise.reject(error);
});
// 응답 인터셉터를 설정하여 응답 중 액세스 토큰 만료 시 리프레시 토큰을 사용해 재시도합니다.
request.interceptors.response.use(undefined, responseError);

// 요청 인터셉터를 설정하여 요청 전에 액세스 토큰을 추가합니다.
// authRequest.interceptors.request.use(requestSuccess, (error) => {
//   return Promise.reject(error);
// });

export default request;
